<template>
  <div class="intent-regular-mode">
    <div class="intent-tag-card">
      <div class="intent-tag-card__head">
        <div class="intent-tag-card__title">
          <span class="intent-tag-title-icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              focusable="false"
              width="1em"
              height="1em"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"
              />
              <circle cx="7.5" cy="7.5" r="1" fill="currentColor" />
            </svg>
          </span>
          <span class="intent-tag-card__title-text">意向标签</span>
        </div>
        <Button type="text" class="intent-tag-add-trigger" @click="openAddPanel()">
          + 添加意向
        </Button>
      </div>

      <div class="intent-tag-tip">
        意向标签用于通话结束后自动判定客户意向类别，帮助快速筛选和分类通话结果。
      </div>

      <div
        v-show="showAddPanel"
        class="intent-tag-add-panel"
        :class="{ 'intent-tag-add-panel--standalone': !intentTag.rules.length }"
      >
        <div class="intent-tag-add-panel__row">
          <Input
            v-model="draftName"
            class="intent-tag-add-panel__name"
            placeholder="标签名称，如 A完成回访"
            :maxlength="80"
          />
          <div
            class="intent-tag-triple"
            :class="draftFormType === INTENT_FORM_TYPE.SYSTEM ? 'is-system' : 'is-prompt'"
          >
            <Select
              v-model="draftFormType"
              class="intent-tag-triple__type"
              transfer
              transfer-class-name="intent-tag-select-dropdown"
              @on-change="onFormTypeChange"
            >
              <Option
                v-for="opt in INTENT_FORM_TYPE_OPTIONS"
                :key="opt.value"
                :value="opt.value"
                :label="opt.label"
              />
            </Select>

            <template v-if="draftFormType === INTENT_FORM_TYPE.PROMPT">
              <Input
                v-model="draftDescription"
                type="textarea"
                class="intent-tag-triple__value"
                :rows="1"
                :autosize="{ minRows: 1, maxRows: 6 }"
                placeholder="标签描述，说明何时应打上该意向"
                :maxlength="1000"
                show-word-limit
              />
            </template>

            <template v-else>
              <Select
                v-model="draftFieldId"
                class="intent-tag-triple__field"
                placeholder="系统指标"
                transfer
                transfer-class-name="intent-tag-select-dropdown"
                @on-change="onFieldChange"
              >
                <Option
                  v-for="field in INTENT_SYSTEM_FIELDS"
                  :key="field.fieldId"
                  :value="field.fieldId"
                  :label="field.name"
                />
              </Select>
              <Select
                v-model="draftOperator"
                class="intent-tag-triple__op"
                transfer
                transfer-class-name="intent-tag-select-dropdown"
              >
                <Option
                  v-for="op in draftOperatorOptions"
                  :key="op"
                  :value="op"
                  :label="getIntentOperatorLabel(op)"
                />
              </Select>
              <Select
                v-if="draftField && draftField.type === EXTRACT_FIELD_TYPE.ENUM"
                v-model="draftValue"
                class="intent-tag-triple__value"
                placeholder="请选择"
                transfer
                transfer-class-name="intent-tag-select-dropdown"
              >
                <Option
                  v-for="val in draftField.allowedValues"
                  :key="val"
                  :value="val"
                  :label="val"
                />
              </Select>
              <Input
                v-else
                v-model="draftValue"
                class="intent-tag-triple__value"
                :type="draftField && draftField.type === EXTRACT_FIELD_TYPE.NUMBER ? 'number' : 'text'"
                :placeholder="draftValuePlaceholder"
                :min="draftField && draftField.type === EXTRACT_FIELD_TYPE.NUMBER ? 0 : undefined"
              />
            </template>
          </div>

          <div class="intent-tag-add-panel__actions">
            <Button type="primary" class="intent-tag-confirm-btn" @click="confirmAdd">
              {{ editingIndex >= 0 ? '保存' : '添加' }}
            </Button>
            <Button type="text" class="intent-tag-cancel-btn" @click="cancelAdd">取消</Button>
          </div>
        </div>
      </div>

      <draggable
        v-if="intentRules.length"
        v-model="intentRules"
        handle=".intent-tag-item__drag"
        class="intent-tag-list"
        :animation="180"
        ghost-class="intent-tag-item--ghost"
        chosen-class="intent-tag-item--chosen"
        @end="resetIntentRankings"
      >
        <div
          v-for="(row, index) in intentRules"
          :key="row.id || 'intent-rule-' + index"
          class="intent-tag-item"
        >
          <span class="intent-tag-item__drag" title="拖拽排序">
            <span class="intent-tag-item__drag-dot" />
            <span class="intent-tag-item__drag-dot" />
            <span class="intent-tag-item__drag-dot" />
            <span class="intent-tag-item__drag-dot" />
            <span class="intent-tag-item__drag-dot" />
            <span class="intent-tag-item__drag-dot" />
          </span>
          <span class="intent-tag-item__rank">{{ index + 1 }}</span>
          <div class="intent-tag-item__name-col">
            <span class="intent-tag-item__name">{{ row.name || '—' }}</span>
            <span
              class="intent-tag-item__badge"
              :class="row.judge === INTENT_JUDGE_TYPE.RULE ? 'is-rule' : 'is-prompt'"
            >
              {{ row.judge === INTENT_JUDGE_TYPE.RULE ? '规则' : '提示词' }}
            </span>
          </div>
          <div class="intent-tag-item__desc-col">{{ formatIntentTagSummary(row) }}</div>
          <div class="intent-tag-item__actions">
            <EditIconButton size="sm" title="编辑" @click="openAddPanel(index)" />
            <Poptip
              :value="exclusiveConfirmKey === 'intent-tag-' + index"
              confirm
              placement="top-end"
              transfer
              :title="rowDeleteTitle(row)"
              ok-text="确定"
              cancel-text="取消"
              @click.native.stop
              @input="onExclusiveConfirmInput('intent-tag-' + index, $event)"
              @on-ok="removeRow(index)"
            >
              <span class="intent-tag-del-trigger">
                <DeleteIconButton size="sm" title="删除" />
              </span>
            </Poptip>
          </div>
        </div>
      </draggable>

      <div v-if="!intentTag.rules.length && !showAddPanel" class="intent-tag-empty">
        暂无意向标签。可添加提示词型（模型判定）或规则型（系统指标，如通话时长 ≤ 15 秒）。
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import {
  EXTRACT_FIELD_TYPE,
  INTENT_FORM_TYPE,
  INTENT_FORM_TYPE_OPTIONS,
  INTENT_JUDGE_TYPE,
  INTENT_SYSTEM_FIELDS,
} from '../../constants/intentTag'
import {
  defaultOpForSystemFieldId,
  formatIntentTagSummary,
  generateIntentTagId,
  getIntentOperatorLabel,
  getOpsForSystemField,
  getSystemFieldById,
  getSystemFieldValuePlaceholder,
  sortItemsByRanking,
} from '../../utils/intentTagHelpers'
import EditIconButton from '../shared/EditIconButton.vue'
import DeleteIconButton from '../shared/DeleteIconButton.vue'
import { exclusiveConfirmMixin } from '../../utils/exclusiveConfirm'

export default {
  name: 'IntentRegularModePanel',
  mixins: [exclusiveConfirmMixin],
  components: {
    draggable,
    EditIconButton,
    DeleteIconButton,
  },
  props: {
    intentTag: {
      type: Object,
      required: true,
    },
  },
  data() {
    const firstField = INTENT_SYSTEM_FIELDS[0]
    return {
      EXTRACT_FIELD_TYPE,
      INTENT_FORM_TYPE,
      INTENT_FORM_TYPE_OPTIONS,
      INTENT_JUDGE_TYPE,
      INTENT_SYSTEM_FIELDS,
      addPanelVisible: false,
      editingIndex: -1,
      draftName: '',
      draftFormType: INTENT_FORM_TYPE.PROMPT,
      draftDescription: '',
      draftFieldId: firstField ? firstField.fieldId : '',
      draftOperator: firstField ? defaultOpForSystemFieldId(firstField.fieldId) : 'eq',
      draftValue: '',
    }
  },
  computed: {
    showAddPanel() {
      return this.addPanelVisible
    },
    draftField() {
      return getSystemFieldById(this.draftFieldId)
    },
    draftOperatorOptions() {
      return getOpsForSystemField(this.draftField)
    },
    draftValuePlaceholder() {
      return getSystemFieldValuePlaceholder(this.draftField)
    },
    intentRules: {
      get() {
        return Array.isArray(this.intentTag.rules) ? this.intentTag.rules : []
      },
      set(list) {
        this.$set(this.intentTag, 'rules', Array.isArray(list) ? list : [])
        this.resetIntentRankings()
      },
    },
  },
  mounted() {
    this.ensureRulesArray()
  },
  watch: {
    'intentTag.rules.length': {
      handler(len) {
        if (this.editingIndex >= 0) return
        this.addPanelVisible = len === 0
      },
      immediate: true,
    },
    draftOperatorOptions(ops) {
      if (ops.length && !ops.includes(this.draftOperator)) {
        this.draftOperator = ops[0]
      }
    },
  },
  methods: {
    getIntentOperatorLabel,
    formatIntentTagSummary,
    ensureRulesArray() {
      if (!Array.isArray(this.intentTag.rules)) {
        this.$set(this.intentTag, 'rules', [])
      }
      sortItemsByRanking(this.intentTag.rules)
      this.resetIntentRankings()
    },
    resetIntentRankings() {
      const rules = Array.isArray(this.intentTag.rules) ? this.intentTag.rules : []
      rules.forEach((rule, index) => {
        if (rule && typeof rule === 'object') {
          this.$set(rule, 'ranking', index + 1)
        }
      })
    },
    resetDraft() {
      const firstField = INTENT_SYSTEM_FIELDS[0]
      this.draftName = ''
      this.draftFormType = INTENT_FORM_TYPE.PROMPT
      this.draftDescription = ''
      this.draftFieldId = firstField ? firstField.fieldId : ''
      this.draftOperator = firstField ? defaultOpForSystemFieldId(firstField.fieldId) : 'eq'
      this.draftValue = ''
      this.editingIndex = -1
    },
    openAddPanel(index) {
      this.ensureRulesArray()
      this.addPanelVisible = true
      if (typeof index === 'number' && index >= 0) {
        const row = this.intentTag.rules[index]
        if (!row) return
        this.editingIndex = index
        this.draftName = row.name || ''
        if (row.judge === INTENT_JUDGE_TYPE.RULE) {
          this.draftFormType = INTENT_FORM_TYPE.SYSTEM
          const cond = Array.isArray(row.conditions) && row.conditions[0] ? row.conditions[0] : null
          this.draftFieldId = cond && cond.fieldId ? cond.fieldId : (INTENT_SYSTEM_FIELDS[0] || {}).fieldId || ''
          this.draftOperator = cond && cond.operator ? cond.operator : defaultOpForSystemFieldId(this.draftFieldId)
          this.draftValue = cond && cond.value != null ? String(cond.value) : ''
          this.draftDescription = ''
        } else {
          this.draftFormType = INTENT_FORM_TYPE.PROMPT
          this.draftDescription = row.rule || ''
        }
      } else {
        this.resetDraft()
      }
      this.$nextTick(() => {
        try {
          const input = this.$el.querySelector('.intent-tag-add-panel__name input')
          if (input && typeof input.focus === 'function') input.focus()
        } catch (e) {
          /* ignore */
        }
      })
    },
    cancelAdd() {
      this.resetDraft()
      this.addPanelVisible = false
    },
    onFormTypeChange(nextType) {
      if (nextType === INTENT_FORM_TYPE.PROMPT) {
        this.draftFieldId = (INTENT_SYSTEM_FIELDS[0] || {}).fieldId || ''
        this.draftOperator = defaultOpForSystemFieldId(this.draftFieldId)
        this.draftValue = ''
      } else {
        this.draftDescription = ''
        const firstField = INTENT_SYSTEM_FIELDS[0]
        this.draftFieldId = firstField ? firstField.fieldId : ''
        this.draftOperator = firstField ? defaultOpForSystemFieldId(firstField.fieldId) : 'eq'
        this.draftValue = ''
      }
    },
    onFieldChange(fieldId) {
      this.draftOperator = defaultOpForSystemFieldId(fieldId)
      this.draftValue = ''
    },
    confirmAdd() {
      const name = String(this.draftName || '').trim()
      if (!name) {
        this.$Message.warning('请填写标签名称')
        return
      }

      const isPrompt = this.draftFormType === INTENT_FORM_TYPE.PROMPT
      let payload

      if (isPrompt) {
        const rule = String(this.draftDescription || '').trim()
        if (!rule) {
          this.$Message.warning('请填写标签描述')
          return
        }
        payload = {
          id: this.editingIndex >= 0 && this.intentTag.rules[this.editingIndex]
            ? this.intentTag.rules[this.editingIndex].id
            : generateIntentTagId(),
          name,
          judge: INTENT_JUDGE_TYPE.PROMPT,
          rule,
          conditions: [],
          ranking: this.editingIndex >= 0 ? this.editingIndex + 1 : this.intentRules.length + 1,
        }
      } else {
        const fieldId = String(this.draftFieldId || '').trim()
        if (!fieldId) {
          this.$Message.warning('请选择系统指标')
          return
        }
        const value = String(this.draftValue || '').trim()
        if (!value) {
          this.$Message.warning('请填写或选择指标取值')
          return
        }
        payload = {
          id: this.editingIndex >= 0 && this.intentTag.rules[this.editingIndex]
            ? this.intentTag.rules[this.editingIndex].id
            : generateIntentTagId(),
          name,
          judge: INTENT_JUDGE_TYPE.RULE,
          rule: '',
          conditions: [
            {
              fieldId,
              operator: this.draftOperator,
              value,
            },
          ],
          ranking: this.editingIndex >= 0 ? this.editingIndex + 1 : this.intentRules.length + 1,
        }
      }

      this.ensureRulesArray()
      if (this.editingIndex >= 0) {
        this.$set(this.intentTag.rules, this.editingIndex, payload)
      } else {
        this.intentTag.rules.push(payload)
      }
      this.resetIntentRankings()
      this.resetDraft()
      this.addPanelVisible = false
    },
    removeRow(index) {
      if (!Array.isArray(this.intentTag.rules)) return
      this.closeExclusiveConfirm('intent-tag-' + index)
      this.intentTag.rules.splice(index, 1)
      this.resetIntentRankings()
      if (this.editingIndex === index) {
        this.resetDraft()
        this.addPanelVisible = false
      }
    },
    rowDeleteTitle(row) {
      const name = row && row.name ? String(row.name).trim() : ''
      return name ? `确定删除意向「${name}」吗？` : '确定删除该条意向标签吗？'
    },
  },
}
</script>

<style lang="scss" scoped>
.intent-tag-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px 20px 24px;
}

.intent-tag-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.intent-tag-card__title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.intent-tag-title-icon {
  display: inline-flex;
  margin-top: 1px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: #0369a1;

  svg {
    display: block;
    width: 16px;
    height: 16px;
  }
}

.intent-tag-card__title-text {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.intent-tag-add-trigger {
  padding: 4px 8px !important;
  height: auto !important;
  color: #5668f0 !important;
  font-size: 14px !important;
  border-radius: 4px;
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover,
  &:focus {
    color: #3d52e8 !important;
    background: #eef1ff !important;
  }
}

.intent-tag-tip {
  margin-bottom: 16px;
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  line-height: 1.6;
  color: #5668f0;
  background: #f5f7ff;
  border: 1px solid #c5ccf8;
  border-radius: 6px;
}

.intent-tag-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.intent-tag-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #c5ccf8;
    box-shadow: 0 1px 4px rgba(86, 104, 240, 0.06);
  }
}

.intent-tag-item__drag {
  cursor: grab;
  display: grid;
  grid-template-columns: repeat(2, 4px);
  grid-template-rows: repeat(3, 4px);
  gap: 0;
  flex-shrink: 0;
  padding: 4px 2px;
  align-self: center;

  &:active {
    cursor: grabbing;
  }
}

.intent-tag-item__drag-dot {
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: #bfbfbf;
}

.intent-tag-item--ghost {
  opacity: 0.55;
}

.intent-tag-item--chosen .intent-tag-item__drag {
  cursor: grabbing;
}

.intent-tag-item__rank {
  flex-shrink: 0;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #eef0ff;
  color: #5668f0;
  font-size: 12px;
  font-weight: 600;
  line-height: 24px;
  text-align: center;
}

.intent-tag-item__name-col {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 168px;
  flex-shrink: 0;
}

.intent-tag-item__name {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  line-height: 1.5;
}

.intent-tag-item__badge {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;

  &.is-prompt {
    color: #5668f0;
    background: #eef1ff;
  }

  &.is-rule {
    color: #b45309;
    background: #fff7ed;
  }
}

.intent-tag-item__desc-col {
  flex: 1;
  min-width: 0;
  padding-top: 1px;
  font-size: 14px;
  line-height: 1.6;
  color: #595959;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
}

.intent-tag-item__actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-top: 1px;
}

.intent-tag-del-trigger {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}

.intent-tag-add-panel {
  margin-top: 0;
  padding: 14px 16px;
  border: 1px solid #b3d8ff;
  border-radius: 8px;
  background: #fff;

  &--standalone {
    margin-top: 0;
  }
}

.intent-tag-add-panel__row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.intent-tag-add-panel__name {
  width: 168px;
  flex-shrink: 0;
}

.intent-tag-add-panel__name ::v-deep input.ivu-input {
  font-weight: 600;
  font-size: 14px;
  height: 32px;
  line-height: 32px;
  text-align: left;
}

.intent-tag-triple {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  gap: 8px;
  align-items: center;
}

.intent-tag-triple__type {
  width: 104px;
  flex-shrink: 0;
}

.intent-tag-triple__field {
  width: 140px;
  flex-shrink: 0;
}

.intent-tag-triple__op {
  width: 112px;
  flex-shrink: 0;
}

.intent-tag-triple.is-prompt .intent-tag-triple__value {
  flex: 1 1 auto;
  min-width: 140px;
}

.intent-tag-triple.is-system .intent-tag-triple__value {
  width: 100px;
  flex-shrink: 0;
}

.intent-tag-triple__type,
.intent-tag-triple__field,
.intent-tag-triple__op,
.intent-tag-triple__value {
  ::v-deep .ivu-select-selection,
  ::v-deep .ivu-input,
  ::v-deep textarea.ivu-input {
    font-size: 14px;
    min-height: 32px;
    text-align: left;
  }

  ::v-deep .ivu-input {
    height: 32px;
    line-height: 32px;
  }

  ::v-deep textarea.ivu-input {
    line-height: 1.5;
    padding: 5px 10px 6px;
    min-height: 32px;
  }

  ::v-deep .ivu-select-selection {
    height: 32px;
    text-align: left;
  }

  ::v-deep .ivu-select-selected-value,
  ::v-deep .ivu-select-placeholder {
    line-height: 30px;
    font-size: 14px;
    text-align: left;
    padding-left: 10px;
    padding-right: 24px;
  }

  ::v-deep .ivu-input-word-count {
    font-size: 14px;
    right: 8px;
    bottom: 4px;
  }

  ::v-deep input[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  ::v-deep input[type='number']::-webkit-outer-spin-button,
  ::v-deep input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.intent-tag-add-panel__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-top: 0;
}

.intent-tag-confirm-btn.ivu-btn {
  height: 32px !important;
  padding: 0 14px !important;
  line-height: 30px;
  font-size: 14px;
}

.intent-tag-cancel-btn.ivu-btn {
  height: 32px !important;
  padding: 0 8px !important;
  line-height: 30px;
  color: #595959 !important;
  font-size: 14px;

  &:hover,
  &:focus {
    color: #0369a1 !important;
    background-color: transparent !important;
    border-color: transparent !important;
    box-shadow: none !important;
  }
}

.intent-tag-empty {
  margin-top: 16px;
  padding: 28px 16px;
  text-align: center;
  font-size: 14px;
  line-height: 1.6;
  color: #8c8c8c;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  background: #fafafa;
}
</style>

<style lang="scss">
.intent-tag-select-dropdown.ivu-select-dropdown {
  text-align: left;

  .ivu-select-item {
    text-align: left;
    font-size: 14px;
    padding: 7px 12px;
  }

  .ivu-select-item-selected,
  .ivu-select-item-selected:hover {
    color: #5668f0;
    font-weight: 500;
  }
}
</style>
